import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Info } from 'lucide-react';
import { supabase } from '../lib/supabase';

type CallbackStatus = 'loading' | 'processing' | 'success' | 'error' | 'pending';

export default function PaymentCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<CallbackStatus>('loading');
  const [message, setMessage] = useState('');
  const [orderId, setOrderId] = useState<string | null>(null);
  const [realOrderId, setRealOrderId] = useState<string | null>(null);

  useEffect(() => {
    void processCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Para el nuevo flujo con checkout_sessions:
   * El orderId que recibimos en el callback es en realidad el checkoutSessionId
   * Debemos buscar primero en checkout_sessions para ver si ya se creó el pedido real
   */
  const pollCheckoutSession = async (sessionId: string) => {
    const maxAttempts = 10;
    const delayMs = 2500;

    setStatus('processing');
    setMessage('Confirmando el estado de tu pago con Recurrente, esto puede tardar unos segundos...');

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Primero, buscar si existe una checkout_session con este ID
      const { data: session, error: sessionError } = await supabase
        .from('checkout_sessions')
        .select('id, status, user_id')
        .eq('id', sessionId)
        .single();

      if (sessionError && sessionError.code !== 'PGRST116') {
        console.error('Error consultando checkout_session:', sessionError);
      }

      // Si la sesión existe y está completada, buscar el pedido real creado por el webhook
      if (session?.status === 'completed') {
        // Buscar el pedido más reciente del usuario creado después de la sesión
        const { data: orders, error: ordersError } = await supabase
          .from('orders')
          .select('id, payment_status, status, created_at')
          .eq('user_id', session.user_id)
          .eq('payment_status', 'paid')
          .order('created_at', { ascending: false })
          .limit(1);

        if (!ordersError && orders && orders.length > 0) {
          const realOrder = orders[0];
          setRealOrderId(realOrder.id);
          setStatus('success');
          setMessage('¡Pago confirmado! Estamos preparando tu pedido.');
          setTimeout(() => {
            navigate(`/confirmation/${realOrder.id}`);
          }, 3000);
          return;
        }
      }

      // Si la sesión falló
      if (session?.status === 'failed') {
        setStatus('error');
        setMessage('El pago fue rechazado por Recurrente. Tu carrito sigue intacto, puedes intentar nuevamente.');
        setTimeout(() => {
          navigate('/checkout');
        }, 5000);
        return;
      }

      // Si no hay checkout_session, buscar como pedido directo (flujo legacy)
      if (!session) {
        const { data: order, error: orderError } = await supabase
          .from('orders')
          .select('payment_status, status')
          .eq('id', sessionId)
          .single();

        if (!orderError && order) {
          if (order.payment_status === 'paid') {
            setRealOrderId(sessionId);
            setStatus('success');
            setMessage('¡Pago confirmado! Estamos preparando tu pedido.');
            setTimeout(() => {
              navigate(`/confirmation/${sessionId}`);
            }, 3000);
            return;
          }

          if (order.payment_status === 'failed') {
            setStatus('error');
            setMessage('El pago fue rechazado por Recurrente. Puedes intentar nuevamente.');
            setTimeout(() => {
              navigate(`/order/${sessionId}/payment?error=payment_failed`);
            }, 5000);
            return;
          }

          if (order.payment_status === 'refunded') {
            setStatus('error');
            setMessage('El pago fue revertido. Si necesitas ayuda contáctanos.');
            setTimeout(() => {
              navigate(`/order/${sessionId}`);
            }, 5000);
            return;
          }
        }
      }

      if (attempt < maxAttempts - 1) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }

    // Si después de todos los intentos no se confirmó el pago
    setStatus('pending');
    setMessage('Tu pago aún está siendo validado por Recurrente. Te notificaremos en cuanto se confirme. Tu carrito sigue guardado.');
    setTimeout(() => {
      navigate('/orders');
    }, 6000);
  };

  const processCallback = async () => {
    try {
      const orderIdParam = searchParams.get('orderId');

      if (!orderIdParam) {
        setStatus('error');
        setMessage('No se encontró la referencia del pedido. Contáctanos para validar tu pago.');
        return;
      }

      setOrderId(orderIdParam);
      await pollCheckoutSession(orderIdParam);
    } catch (error) {
      console.error('Error procesando callback Recurrente:', error);
      setStatus('error');
      setMessage('No pudimos validar el estado de tu pago. Por favor revisa tus pedidos o contáctanos.');
    }
  };

  const renderIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle className="mx-auto h-16 w-16 text-green-600" />;
      case 'error':
        return <XCircle className="mx-auto h-16 w-16 text-red-600" />;
      case 'pending':
        return <Info className="mx-auto h-16 w-16 text-amber-500" />;
      default:
        return <Loader2 className="mx-auto h-16 w-16 text-blue-600 animate-spin" />;
    }
  };

  const renderHeading = () => {
    switch (status) {
      case 'success':
        return '¡Pago exitoso con Recurrente!';
      case 'error':
        return 'No pudimos completar tu pago';
      case 'pending':
        return 'Pago en validación';
      case 'processing':
        return 'Confirmando tu pago...';
      default:
        return 'Procesando pago...';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center space-y-6">
          {renderIcon()}
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {renderHeading()}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {message}
            </p>
          </div>

          {status === 'error' && (
            <div className="space-y-3">
              <button
                onClick={() => navigate('/checkout')}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                Intentar pagar nuevamente
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200"
              >
                Volver al inicio
              </button>
            </div>
          )}

          {status === 'pending' && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">
                Te enviaremos una notificación cuando Recurrente confirme el pago. Tu carrito sigue guardado por si necesitas intentar nuevamente.
              </p>
              <button
                onClick={() => navigate('/orders')}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                Ver mis pedidos
              </button>
              <button
                onClick={() => navigate('/checkout')}
                className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200"
              >
                Volver al checkout
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
